import axios from "axios";
import moment from "moment";
import { loadStaxScriptFromEnv } from "../../utils/loadStaxScript";

/**
 * StaxProcessor: Handles Stax payment gateway integration
 * - Initializes StaxJs library with FM_KEY
 * - Tokenizes payment data via Stax's client-side SDK
 * - Submits charges to backend for server-side processing
 */
class StaxProcessor {
  constructor() {
    this.staxJs = null;
    this.fmKey = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Stax payment processor
   * @param {Object} options - Initialization options
   * @param {string} options.domain - Domain for fetching payment config
   * @param {Function} options.onCardFormComplete - Callback when card form is complete
   * @param {Function} options.onCardFormUncomplete - Callback when card form is incomplete
   * @returns {Promise<boolean>} - Success status
   */
  async initialize({ domain, onCardFormComplete, onCardFormUncomplete }) {
    try {
      // Ensure Stax SDK is loaded from env-configured URL
      await loadStaxScriptFromEnv();

      if (window.console && window.console.log) {
        window.console.log('[StaxProcessor] initialize start', { domain });
      }
      // Fetch FM_KEY from backend
      const response = await axios.post(
        `${process.env.REACT_APP_API || 'https://dev.beclutch.club/api'}/camp/get_public_payment_config`,
        { domain }
      );

      if (window.console && window.console.log) {
        window.console.log('[StaxProcessor] payment config response', response?.data);
      }
      if (!response.data.success || !response.data.stax_fm_key) {
        throw new Error('Failed to load Stax payment configuration');
      }

      this.fmKey = response.data.stax_fm_key;
      if (window.console && window.console.log) {
        window.console.log('[StaxProcessor] fmKey loaded (first 10 chars):', this.fmKey?.substring(0, 10));
      }

      // Initialize StaxJs library with slight delay for DOM readiness
      await new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            const StaxJs = window.StaxJs;
            if (window.console && window.console.log) {
              window.console.log('[StaxProcessor] window.StaxJs check:', { exists: !!StaxJs });
            }
            if (!StaxJs) {
              return reject(new Error('StaxJs library not loaded'));
            }

            if (window.console && window.console.log) {
              window.console.log('[StaxProcessor] Creating new StaxJs instance...');
            }

            this.staxJs = new StaxJs(this.fmKey, {
              number: {
                id: "card-number",
                placeholder: "0000 0000 0000 0000",
                style:
                  "width: 100%; height:100%; border-radius: 3px; border: 1px solid #ccc; padding: .5em .5em; font-size: 91%; box-sizing: border-box;",
              },
              cvv: {
                id: "card-cvv",
                placeholder: "CVV",
                style:
                  "width: 100%; height:100%; border-radius: 3px; border: 1px solid #ccc; padding: .5em .5em; font-size: 91%; box-sizing: border-box;",
              },
            });

            if (window.console && window.console.log) {
              window.console.log('[StaxProcessor] StaxJs instance created:', { hasInstance: !!this.staxJs });
            }

            // Set up event listeners
            if (onCardFormComplete) {
              this.staxJs.on("card_form_complete", onCardFormComplete);
            }

            if (onCardFormUncomplete) {
              this.staxJs.on("card_form_incomplete", onCardFormUncomplete);
            }

            this.isInitialized = true;
            if (window.console && window.console.log) {
              window.console.log('[StaxProcessor] initialized successfully, isInitialized:', this.isInitialized);
            }
            resolve();
          } catch (e) {
            if (window.console && window.console.error) {
              window.console.error('[StaxProcessor] initialization error', e);
            }
            reject(e);
          }
        }, 200);
      });

      return true;
    } catch (error) {
      console.error('Failed to initialize Stax:', error);
      throw error;
    }
  }

  /**
   * Process payment via Stax (tokenization + charge)
   * @param {Object} paymentData - Payment information
   * @returns {Promise<Object>} - Payment result with transaction details
   */
  async processPayment(paymentData) {
    if (!this.isInitialized || !this.staxJs) {
      throw new Error('Stax processor not initialized');
    }

    const {
      cardType,
      userData,
      registrationInfo,
      totalCheckout,
    } = paymentData;

    // Build payment details for Stax.pay()
    const payDetails = {
      total: parseFloat(totalCheckout).toFixed(2),
      firstname: userData.bill_first_name,
      lastname: userData.bill_last_name,
      email: userData.p_email,
      phone: userData.p_cell_phone,
      address_1: userData.bill_address,
      address_2: userData.bill_address_2 || "",
      address_city: userData.bill_city,
      address_state: userData.bill_state,
      address_zip: userData.bill_postal_code.toString(),
      address_country: "USA",
      send_receipt: true,
      url: "https://app.staxpayments.com/#/bill/",
      ...(cardType === "echeck"
        ? {
            method: "bank",
            bank_name: userData.bank_name,
            bank_type: userData.bank_type,
            bank_holder_type: userData.bank_holder_type,
            bank_account: userData.bank_account,
            bank_routing: userData.bank_routing,
            person_name: userData.first_name + " " + userData.last_name,
          }
        : {
            method: "card",
            month: moment(userData.expiration, "MM/YY").format("MM"),
            year: moment(userData.expiration, "MM/YY").format("YYYY"),
          }),
      validate: false,
      meta: {
        reference: registrationInfo?.registration_name + " Fee",
        isCCPaymentEnabled: true,
        isACHPaymentEnabled: true,
        isTipEnabled: true,
      },
    };

    // pay() tokenizes and charges in one step — no separate backend charge needed
    const payResult = await this.staxJs.pay(payDetails);

    const { payment_method_id, customer_id } = payResult || {};
    if (!payment_method_id) {
      throw new Error("Missing payment_method_id from Stax response");
    }

    return {
      success: true,
      transactionId: payment_method_id,
      customerId: customer_id,
      paymentMethodId: payment_method_id,
      data: payResult,
    };
  }

  /**
   * Show card form (for card payments)
   * Waits for DOM elements to be ready before injecting iframes
   */
  showCardForm() {
    // Debug: make this visible
    if (window.console && window.console.log) {
      window.console.log('[StaxProcessor] showCardForm() called', {
        hasStaxJs: !!this.staxJs,
        isInitialized: this.isInitialized
      });
    }

    if (!this.staxJs || !this.isInitialized) {
      if (window.console && window.console.log) {
        window.console.log('[StaxProcessor] showCardForm called but not initialized');
      }
      return;
    }

    const maxAttempts = 20;
    const retryDelay = 250;
    let attempts = 0;
    const self = this;

    const tryShowForm = () => {
      attempts++;
      const cardNumberEl = document.getElementById('card-number');
      const cardCvvEl = document.getElementById('card-cvv');

      if (window.console && window.console.log) {
        window.console.log(`[StaxProcessor] showCardForm attempt ${attempts}`, {
          cardNumberExists: !!cardNumberEl,
          cardCvvExists: !!cardCvvEl,
          cardNumberChildren: cardNumberEl?.children?.length || 0,
          cardCvvChildren: cardCvvEl?.children?.length || 0
        });
      }

      if (cardNumberEl && cardCvvEl) {
        // DOM elements exist, inject the iframes
        if (window.console && window.console.log) {
          window.console.log('[StaxProcessor] DOM elements found, calling staxJs.showCardForm()');
        }

        try {
          self.staxJs.showCardForm();
        } catch (err) {
          if (window.console && window.console.error) {
            window.console.error('[StaxProcessor] Error calling staxJs.showCardForm():', err);
          }
        }

        // Verify injection after a short delay
        setTimeout(() => {
          const injected = cardNumberEl.children.length > 0 && cardCvvEl.children.length > 0;
          if (window.console && window.console.log) {
            window.console.log('[StaxProcessor] Injection verification:', {
              injected,
              cardNumberChildren: cardNumberEl.children.length,
              cardCvvChildren: cardCvvEl.children.length
            });
          }
          if (!injected && attempts < maxAttempts) {
            if (window.console && window.console.log) {
              window.console.log('[StaxProcessor] Injection failed, retrying...');
            }
            setTimeout(tryShowForm, retryDelay);
          }
        }, 100);
      } else if (attempts < maxAttempts) {
        // DOM elements not ready, retry
        if (window.console && window.console.log) {
          window.console.log('[StaxProcessor] DOM elements not ready, retrying...');
        }
        setTimeout(tryShowForm, retryDelay);
      } else {
        if (window.console && window.console.error) {
          window.console.error('[StaxProcessor] Max attempts reached, DOM elements never found');
        }
      }
    };

    tryShowForm();
  }

  /**
   * Show bank form (for eCheck payments)
   */
  showBankForm() {
    if (this.staxJs && this.isInitialized) {
      this.staxJs.showBankForm();
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.staxJs = null;
    this.fmKey = null;
    this.isInitialized = false;
  }
}

export default StaxProcessor;
